import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Container, Form, Nav, Row, Toast } from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import { useMyPlayer } from "../context/MyPlayerContext";
import PlayerTitle from "../components/player/PlayerTitle";
import PlayerEpicStats from "../components/epicLeaderboard/PlayerEpicStats";
import PlayerGquestStats from "../components/player/PlayerGquestStats";
import PlayerOverviewTab from "../components/player/PlayerOverviewTab";
import PlayerPkTab from "../components/player/pk/PlayerPkTab";
import PlayerRangeBar from "../components/player/PlayerRangeBar";
import PlayerStatsSkeleton from "../components/player/PlayerStatsSkeleton";
import "../components/player/pk/pk.css";
import { DEFAULT_RANGE, RANGE_OPTIONS } from "../constants/epicLeaderboard";
import { useApiResource } from "../hooks/useApiResource";
import { analytics } from "../utils/analytics";
import { playerApiUrl } from "../utils/playerApi";
import {
  PLAYER_TABS,
  parseTab,
  playerSearch,
} from "../utils/playerLink";
import { rangeToStartDate } from "../utils/rangeToStartDate";

const API_HOST = process.env.REACT_APP_API;

const RANGE_PARAM = "range";
// Older links used "epicRange" for the epic section's own range.
const LEGACY_RANGE_PARAM = "epicRange";
const TAB_PARAM = "tab";
const VS_PARAM = "vs";
const FEED_PAGE_PARAM = "feedPage";

const parseRange = (value) =>
  RANGE_OPTIONS.find((o) => o.value === value)?.value ?? DEFAULT_RANGE;

const parseFeedPage = (value) => {
  const page = Number.parseInt(value ?? "", 10);
  return Number.isInteger(page) && page > 1 ? page : 1;
};

const useDebouncedCallback = (callback, delay, opts) => {
  const callbackRef = useRef();
  callbackRef.current = callback;
  return debounce((...args) => callbackRef.current(...args), delay, opts);
};

export const Player = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const range = parseRange(
    searchParams.get(RANGE_PARAM) ?? searchParams.get(LEGACY_RANGE_PARAM)
  );
  const tab = parseTab(searchParams.get(TAB_PARAM));
  const vs = searchParams.get(VS_PARAM);
  const feedPage = parseFeedPage(searchParams.get(FEED_PAGE_PARAM));
  // The URL is the source of truth, so links to another player (opponent
  // names) work without remounting the page.
  const realPlayerName = searchParams.get("player");

  const { myPlayer, isMe } = useMyPlayer();
  const [options, setOptions] = useState([]);
  const [str, setStr] = useState(realPlayerName || "");
  const [showSuggestions, setShowSuggestions] = useState(false);

  // ISO start of the range; computed once per range so the request URLs (and
  // their cached responses) stay stable.
  const startDate = useMemo(() => rangeToStartDate(range), [range]);

  // Only the Overview tab needs the base player stats; the other tabs fetch
  // their own data. Responses are cached per player + range.
  const stats = useApiResource(
    tab === "overview" && realPlayerName
      ? playerApiUrl(realPlayerName, "", { startDate })
      : null,
    (realPlayerName || "").toLowerCase()
  );
  const playerData = stats.data;
  const showNotFound =
    tab === "overview" &&
    !!playerData &&
    playerData.kills === null &&
    playerData.deaths === null;
  const statsRefetching = stats.loading && !!playerData;

  const debouncedOnSearch = useDebouncedCallback(
    async (value) => {
      const data = await (
        await fetch(
          `${API_HOST}public/player-name-suggestions?partialName=${value}`
        )
      ).json();
      setOptions(data);
      setShowSuggestions(true);
    },
    500,
    { leading: true, trailing: true }
  );

  const handleInputChange = async ({ target: { value } }) => {
    setStr(value);
    if (value?.length > 2) {
      debouncedOnSearch(value);
    }
  };

  // Enter picks the typed name if it is a suggestion, else the first one.
  const handleInputKeyDown = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    const typed = str.trim();
    // Suggestions linger after a pick; only trust the ones on screen.
    const shown = showSuggestions ? options ?? [] : [];
    const exact = shown.find((o) => o.toLowerCase() === typed.toLowerCase());
    const choice = exact ?? shown[0] ?? typed;
    if (choice) handleSuggestionClick(choice);
  };

  const handleSuggestionClick = (name) => {
    // Merge into the existing params so the selected range and tab survive;
    // the opponent and feed page belong to the previous player.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("player", name);
      next.delete(VS_PARAM);
      next.delete(FEED_PAGE_PARAM);
      return next;
    });
    setShowSuggestions(false);
  };

  // Empties the search and drops the shown player. The saved character is
  // not touched.
  const handleClear = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("player");
      next.delete(VS_PARAM);
      next.delete(FEED_PAGE_PARAM);
      return next;
    });
    setStr("");
    setOptions([]);
    setShowSuggestions(false);
  };

  // Keep the search box in step when the URL's player changes (links, back).
  useEffect(() => {
    setStr(realPlayerName || "");
    setShowSuggestions(false);
  }, [realPlayerName]);

  const handleRangeChange = (value) => {
    analytics.trackFeatureUsage("Player", "range_select", { range: value });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(LEGACY_RANGE_PARAM);
      next.delete(FEED_PAGE_PARAM);
      if (value === DEFAULT_RANGE) next.delete(RANGE_PARAM);
      else next.set(RANGE_PARAM, value);
      return next;
    });
  };

  const handleVsChange = (opponent) => {
    analytics.trackFeatureUsage("Player", "head_to_head", {
      selected: !!opponent,
    });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (opponent) next.set(VS_PARAM, opponent);
      else next.delete(VS_PARAM);
      return next;
    });
  };

  const handleFeedPageChange = (page) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (page > 1) next.set(FEED_PAGE_PARAM, String(page));
      else next.delete(FEED_PAGE_PARAM);
      return next;
    });
    document
      .getElementById("pk-feed")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    if (realPlayerName) {
      // Send event
      if (process.env.NODE_ENV !== "development") {
        window.gtag("event", "player_search", {
          name: realPlayerName,
        });
      }
    }
  }, [realPlayerName]);

  return (
    <>
      <Container>
        <Row>
          <PlayerTitle playerName={realPlayerName} />
        </Row>
        {realPlayerName && (
          <PlayerRangeBar
            range={range}
            onChange={handleRangeChange}
            loading={stats.loading}
          />
        )}
        <Row className="player-search">
          <Col>
            <Form.Label htmlFor="playername" style={{ color: "#98d8ff" }}>
              Player name
            </Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                id="playername"
                aria-describedby="player name search"
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                value={str}
                className="w-100"
              />
              {myPlayer && !isMe(realPlayerName) && (
                <Button
                  variant="outline-warning"
                  className="text-nowrap"
                  title="Fill in your saved character"
                  onClick={() => handleSuggestionClick(myPlayer)}
                >
                  ★ {myPlayer}
                </Button>
              )}
              {(str || realPlayerName) && (
                <Button
                  variant="outline-secondary"
                  title="Clear the search"
                  onClick={handleClear}
                >
                  Clear
                </Button>
              )}
            </div>
            <Form.Text id="playername help" style={{ color: "#f1f4c6" }}>
              3 characters minimum
            </Form.Text>
            {showSuggestions && (
              <div className="mt-3">
                {options?.map((e, i) => {
                  return (
                    <Button
                      variant="primary"
                      key={i}
                      onClick={() => handleSuggestionClick(e)}
                      className="me-3"
                    >
                      {e}
                    </Button>
                  );
                })}
              </div>
            )}

            {showNotFound && (
              <Toast show={true} animation={true} className="mt-5">
                <Toast.Body>Not found!</Toast.Body>
              </Toast>
            )}
          </Col>
        </Row>

        {realPlayerName && (
          <Nav variant="tabs" className="player-tabs mt-4" as="nav">
            {PLAYER_TABS.map((t) => (
              <Nav.Item key={t.value}>
                <Nav.Link
                  as={Link}
                  to={{
                    search: `?${playerSearch({
                      player: realPlayerName,
                      range,
                      tab: t.value,
                    })}`,
                  }}
                  active={t.value === tab}
                  aria-current={t.value === tab ? "page" : undefined}
                  onClick={() =>
                    t.value !== tab &&
                    analytics.trackFeatureUsage("Player", "tab_select", {
                      tab: t.value,
                    })
                  }
                >
                  {t.label}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        )}

        {realPlayerName && tab === "overview" && (
          <>
            {playerData === null && !stats.error && <PlayerStatsSkeleton />}
            {stats.error && !playerData && (
              <div className="player-muted-note mt-5">
                Couldn't load player stats.{" "}
                <Button
                  size="sm"
                  variant="link"
                  className="p-0"
                  onClick={stats.reload}
                >
                  Retry
                </Button>
              </div>
            )}
            {playerData && !showNotFound && (
              <div className={statsRefetching ? "is-refetching" : undefined}>
                <PlayerOverviewTab
                  player={realPlayerName}
                  range={range}
                  startDate={startDate}
                  stats={playerData}
                />
              </div>
            )}
          </>
        )}

        {realPlayerName && tab === "pk" && (
          <PlayerPkTab
            player={realPlayerName}
            range={range}
            startDate={startDate}
            vs={vs}
            feedPage={feedPage}
            onVsChange={handleVsChange}
            onFeedPageChange={handleFeedPageChange}
          />
        )}

        {realPlayerName && tab === "epics" && (
          <PlayerEpicStats
            playerName={realPlayerName}
            range={range}
            showWhenNoRuns
          />
        )}

        {realPlayerName && tab === "gquests" && (
          <PlayerGquestStats playerName={realPlayerName} startDate={startDate} />
        )}
      </Container>
    </>
  );
};
